import { useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  clonePlan,
  createPlan,
  getActivePlanAssignmentsQueryKey,
  getManagedPlansQueryKey,
  getSystemPlansOptions,
} from '@cfa/pathway-fe-shared/xp';
import { Radio, RadioGroup, TextInput } from '@cfa/react-core';

import useAuth from '@/hooks/useAuth';
import { usePlanOwnerOptions } from '@/hooks/usePlanOwner';

import { extractErrorMessage, extractStatusCode } from '@/utils/error-utils';
import { isForbiddenError } from '@/utils/isAuthError';
import { createCurrentLanguageValue, itemByLanguage } from '@/utils/language';
import { isApiError } from '@/utils/request';

import GenericError from '@/components/Error/GenericError';
import PathwayModal from '@/components/popups/PathwayModal';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import { queryClient } from '@/services';

import InvalidName from './InvalidName';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { buildDuplicatePlanName } from '@/routes/TrainingPlans/buildDuplicatePlanName';
import useIsValidPlanName from '@/routes/TrainingPlans/hooks/useIsValidPlanName';
import { isPlanOwnerEligible } from '@/routes/TrainingPlans/isPlanOwnerEligible';
import { markPlanBuildStartedAtom } from '@/routes/TrainingPlans/planBuildTimer';

interface PlanSetupStepProps {
  onCancel: () => void;
  setShowInvalidNameError: (show: boolean) => void;
  showInvalidNameError?: boolean;
  /** Present = copy mode (clone template); absent = create mode (new blank plan). */
  copiedTemplateId?: string;
}

const CreateOrCopyPlanModalStep: React.FC<PlanSetupStepProps> = ({
  copiedTemplateId,
  setShowInvalidNameError,
  showInvalidNameError = false,
  onCancel,
}) => {
  const isCopyMode = copiedTemplateId !== undefined;

  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const markPlanBuildStarted = useSetAtom(markPlanBuildStartedAtom);
  const { t } = useTranslation();
  const { data } = useAuth();
  const {
    shouldSelectOwner,
    isMultiOwner,
    isFetching,
    planOwners,
    ownerOptions,
    resolvePlanOwner: resolveOwner,
  } = usePlanOwnerOptions({ isEligible: isPlanOwnerEligible });

  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const initialStep = shouldSelectOwner ? 1 : 2;
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>();
  const [ownerSelected, setOwnerSelected] = useState(false);
  const [stepIndex, setStepIndex] = useState(initialStep);

  const allLocationsWithAtLeastLeaderRoles =
    data?.allLocationsWithAtLeastLeaderRoles ?? [];

  const { data: templates, error: templatesError } = useQuery({
    ...getSystemPlansOptions(),
    enabled: isCopyMode && allLocationsWithAtLeastLeaderRoles.length > 0,
  });

  const templateName = isCopyMode
    ? itemByLanguage(
        templates?.find(template => template.id === copiedTemplateId)?.name,
        '',
      )
    : '';

  const [input, setInput] = useState(
    isCopyMode
      ? buildDuplicatePlanName(
          templateName,
          t('TrainingPlans.duplicatePlanInputCopy'),
        )
      : '',
  );

  const effectiveOwnerId =
    selectedOwnerId ??
    (!isMultiOwner && ownerOptions.length === 1
      ? ownerOptions[0]?.ownerId
      : undefined);

  const isValidPlanName = useIsValidPlanName({
    onInvalidName: () => setShowInvalidNameError(true),
  });

  const showSubmitError = (
    messageKey:
      | 'TrainingPlans.toastMessage.errorCopyingPlan'
      | 'TrainingPlans.toastMessage.errorCopyingPlanPermissions'
      | 'TrainingPlans.toastMessage.errorCreatingPlan',
  ) => {
    toast.custom(toastObj => (
      <ToastMessageBlock id={toastObj.id} severity="error">
        {t(messageKey)}
      </ToastMessageBlock>
    ));
  };

  const toError = (err: unknown, fallbackMessage: string) => {
    if (err instanceof Error) {
      const normalizedError = err as Error & { status?: number };
      const extractedStatus = extractStatusCode(err);

      if (extractedStatus !== undefined) {
        normalizedError.status ??= extractedStatus;
      }

      return normalizedError;
    }

    const extractedMessage = extractErrorMessage(err, fallbackMessage);
    const extractedStatus = extractStatusCode(err);
    const normalizedError = new Error(extractedMessage, {
      cause: err,
    }) as Error & {
      status?: number;
    };

    if (extractedStatus !== undefined) {
      normalizedError.status = extractedStatus;
    }

    return normalizedError;
  };

  const toRequestError = (
    err: unknown,
    fallbackMessage: string,
    status?: number,
  ) => {
    const requestError: Error & { status?: number } = toError(
      err,
      fallbackMessage,
    );

    if (status !== undefined) {
      requestError.status ??= status;
    }

    return requestError;
  };

  const logSubmitError = (err: unknown) => {
    const normalizedError = toError(err, 'Training plan submit failed');
    const status = extractStatusCode(err);
    const originalErrorMessage = extractErrorMessage(err, '').trim();

    datadogRum.addError(normalizedError, {
      status,
      originalErrorMessage:
        originalErrorMessage.length > 0 ? originalErrorMessage : undefined,
    });
  };

  const resolvePlanCreatedAt = (created: string | undefined) =>
    typeof created === 'string' && created.length > 0
      ? created
      : new Date().toISOString();

  type OwnerPayload = ReturnType<typeof resolveOwner>;

  const copyPlanMutation = useMutation({
    mutationFn: async (vars: { name: string; ownerPayload: OwnerPayload }) => {
      if (copiedTemplateId === undefined) {
        throw new Error('Missing template id when copying training plan');
      }

      const { data, error, response } = await clonePlan({
        path: { planId: copiedTemplateId },
        body: {
          name: createCurrentLanguageValue(vars.name, userLanguage),
          ...vars.ownerPayload,
        },
      });

      if (!response?.ok) {
        throw toRequestError(
          error,
          `Clone plan request failed with status ${response?.status ?? 'unknown'}`,
          response?.status,
        );
      }

      if (error) {
        throw toError(error, 'Clone plan request failed');
      }

      const planId = data.id;
      if (!planId) {
        throw new Error('Clone plan response missing plan id');
      }

      return { planId, created: data.created };
    },
    onError: logSubmitError,
  });

  const createPlanMutation = useMutation({
    mutationFn: async (vars: { name: string; ownerPayload: OwnerPayload }) => {
      const { data, error } = await createPlan({
        body: {
          name: createCurrentLanguageValue(vars.name, userLanguage),
          ...vars.ownerPayload,
        },
      });

      if (error) {
        throw toError(error, 'Create plan request failed');
      }

      const planId = data.id;
      if (!planId) {
        throw new Error('Create plan response missing plan id');
      }

      // refetch (not invalidate) because the global config sets
      // refetchOnMount: false, so the manage-plans list won't refresh on its own
      // when the user returns. Failures shouldn't block navigation to the editor.
      try {
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: getManagedPlansQueryKey({ query: { status: 'ACTIVE' } }),
          }),
          // In case you assign a Plan to yourself, we do a refetch.
          queryClient.refetchQueries({
            queryKey: getActivePlanAssignmentsQueryKey(),
          }),
        ]);
      } catch (err) {
        logSubmitError(err);
      }

      return { planId, created: data.created };
    },
    onError: logSubmitError,
  });

  const isSubmitting =
    copyPlanMutation.isPending || createPlanMutation.isPending;

  if (isApiError(templatesError)) {
    datadogRum.addError(
      templatesError instanceof Error
        ? templatesError
        : new Error(JSON.stringify(templatesError)),
    );
    return <GenericError />;
  }

  const onSubmit = (name: string) => {
    const trimmedName = name.trim();
    const ownerPayload = resolveOwner(effectiveOwnerId);
    const ownerId = ownerPayload.ownerId;

    if (!ownerId) {
      datadogRum.addError(
        new Error('Missing owner id when adding training plan'),
      );
      return;
    }

    if (!isValidPlanName(trimmedName, ownerId)) {
      return;
    }

    if (isCopyMode) {
      copyPlanMutation.mutate(
        { name: trimmedName, ownerPayload },
        {
          onSuccess: result => {
            track(AmplitudeProductEvent.TRAINING_TEMPLATE_COPIED, {
              template_id: copiedTemplateId,
              operator: ownerId,
            });
            markPlanBuildStarted({
              planId: result.planId,
              startedAt: resolvePlanCreatedAt(result.created),
              source: 'template_copy',
            });
            navigate(routes.plans.managePlansEdit({ planId: result.planId }));
          },
          onError: err =>
            showSubmitError(
              isForbiddenError(err)
                ? 'TrainingPlans.toastMessage.errorCopyingPlanPermissions'
                : 'TrainingPlans.toastMessage.errorCopyingPlan',
            ),
        },
      );
    } else {
      // PATHWAY-3299: custom plan created by IM for their IM COR location
      const selectedOwner = planOwners.find(owner => owner.ownerId === ownerId);
      const isInterimManager =
        selectedOwner?.ownerType === 'LOCATION' &&
        selectedOwner.hasInterimManager;

      createPlanMutation.mutate(
        { name: trimmedName, ownerPayload },
        {
          onSuccess: result => {
            track(AmplitudeProductEvent.TRAINING_PLAN_CREATED, {
              training_plan_id: result.planId,
              owner: ownerId,
              is_interim_manager: isInterimManager,
            });
            markPlanBuildStarted({
              planId: result.planId,
              startedAt: resolvePlanCreatedAt(result.created),
              source: 'blank',
            });
            navigate(routes.plans.managePlansEdit({ planId: result.planId }));
          },
          onError: () =>
            showSubmitError('TrainingPlans.toastMessage.errorCreatingPlan'),
        },
      );
    }
  };

  const selectOwner = (ownerId: string) => {
    setSelectedOwnerId(ownerId);
    setOwnerSelected(true);
  };

  const goToStep2 = () => setStepIndex(2);

  const isStep1 = !isFetching && stepIndex === 1;

  if (showInvalidNameError) {
    return <InvalidName setShowInvalidNameError={setShowInvalidNameError} />;
  }

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>
          {isStep1
            ? t('TrainingPlans.addPlanMenu.chooseOperator')
            : t('Generic.giveName', { object: t('Generic.plan') })}
        </PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        {isStep1 ? (
          <div className="flex justify-center">
            <RadioGroup
              defaultValue=""
              name="operatorList"
              orientation="vertical"
              onChange={selectOwner}
            >
              {ownerOptions.map(owner => (
                <Radio key={owner.ownerId} value={owner.ownerId}>
                  {owner.label}
                </Radio>
              ))}
            </RadioGroup>
          </div>
        ) : (
          <TextInput
            className="form-control"
            name="input"
            onChange={setInput}
            errorMessage={
              input.length
                ? undefined
                : `${t('Generic.planName')} ${t('TrainingPlans.requiredValue')}`
            }
            maxLength={Constants.FORMS.MAX_PLAN_NAME_LENGTH}
            onKeyDown={e => e.key === 'Enter' && onSubmit(input)}
            placeholder={t('TrainingPlans.planNamePlaceholder')}
            value={input}
          />
        )}
      </PathwayModal.Body>
      <PathwayModal.Footer>
        <PathwayModal.SecondaryButton
          color="secondary"
          onPress={() => {
            if (!isStep1 && shouldSelectOwner) {
              setStepIndex(1);
              return;
            }
            onCancel();
          }}
        >
          {isStep1 || !shouldSelectOwner
            ? t('Button.cancel')
            : t('Button.back')}
        </PathwayModal.SecondaryButton>
        <PathwayModal.PrimaryButton
          color="secondary"
          variant="filled"
          onPress={isStep1 ? goToStep2 : () => onSubmit(input)}
          isDisabled={
            isStep1 ? !ownerSelected : input.trim().length === 0 || isSubmitting
          }
        >
          {isStep1 ? t('Button.next') : t('Button.create')}
        </PathwayModal.PrimaryButton>
      </PathwayModal.Footer>
    </>
  );
};

export default CreateOrCopyPlanModalStep;
